import React from 'react';
import { useRouting } from 'hooks/useRouting';
import { useModal } from 'hooks/useModal';
import { useQuery } from 'react-query';
import * as env from 'config/environment';
import * as consts from 'config/consts';
import { reportLoadingStyle } from 'pages/NewReport';
import { LineNoticeBanner } from 'components/features/home/HomeContentsInternal';
import { NoticeSettingModal } from 'components/features/modal/NoticeSettingModal';
import { getUserStorage } from 'components/features/utils/utils';
import Loading from 'components/commons/loading/Loading';
import { isPremium } from 'components/features/utils/userStatus';
import { AssetChange } from './components/AssetChange';
import { TransactionsHistory } from './components/TransactionsHistory';
import { TradeList } from './components/TradeList';
import { GetReportResult, getReport } from 'config/api';
import { getViewedWebPushStorage } from 'utils/storage';

export const yyyymmddToDate = (val: string): Date | null => {
  const regex = /^(\d{4})(\d{2})(\d{2})$/;
  const result = regex.exec(val);
  if (result === null) return null;
  return new Date(Date.parse(`${result[1]}-${result[2]}-${result[3]}`));
};

export const APIFail: React.FC<Record<string, unknown>> = () => {
  return (
    <>
      <div className="bg-white">
        <div className="flex flex-col items-center justify-start py-8 px-6">
          <div
            className="mb-6 text-lg text-center font-bold text-bluegray-700"
            style={{ lineHeight: '18px' }}>
            準備中です
          </div>
          <img src="/static/images/report/report_stand-by.svg" alt="" />
          <div className="w-full mt-6 mb-10 mx-auto text-left text-xs text-black">
            日次レポートは毎日夜発行（土日除く）されます。
            <br />
            初回レポートが作成されるまでもうしばらくお待ち下さい。
          </div>
          <a id="dailyreport_line" href={env.lineAddFriendURL}>
            <img
              src="/static/images/line1.png"
              alt="友だち追加"
              className="h-12.5"
              style={{ width: '265px' }}
            />
          </a>
        </div>
      </div>
    </>
  );
};

export type OrderItemProps = {
  brokerID: string;
  brokerName: string;
  tradeTime: string | null;
  symbol: string;
  symbolName: string;
  accountType: number;
  productType: number;
  cashMargin: number;
  tradeType: number;
  side: number;
  qty: number | null;
  price: number | null;
  openPrice: number | null;
  openPriceUSD: number | null;
  profitOrLoss: number | null;
  profitOrLossUSD: number | null;
  currencyUnit: string | null;
};

export const DailyReport = ({ isLinkedLine }: { isLinkedLine: boolean }) => {
  const userStorage = getUserStorage();
  const viewedWebPushStorage = getViewedWebPushStorage();
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isiPhone = /iphone|ipod/.test(userAgent);
  const isiPad = /ipad|macintosh/.test(userAgent) && 'ontouchend' in document;

  const initialValue =
    !isiPhone &&
    !isiPad &&
    userStorage !== null &&
    !userStorage.RegisteredWebPush &&
    (viewedWebPushStorage === null || !viewedWebPushStorage.dailyReport);

  const [
    isShowNoticeSettingModal,
    { hideModal: hideNoticeSettingModal },
  ] = useModal(initialValue);

  const { pushRouting: pushRouting_ } = useRouting();
  const pushRouting = (path: string): void => pushRouting_({ path }, true);
  const onAccountItemClick = (account: string): void => {
    pushRouting(`/accountdetail/${account}`);
  };
  const onTradeItemClick = (symbol: string): void => {
    pushRouting(`/tradedetail/${symbol}`);
  };

  const { data: reportData } = useQuery('dailyReport', fetchReport, {
    refetchOnWindowFocus: false,
  });

  if (reportData === null || reportData === undefined)
    return <Loading style={reportLoadingStyle} loading={true} />;

  if (reportData.Day === null) return <APIFail />;

  const targetDate =
    reportData.Day !== undefined && reportData.Day.Base.From
      ? yyyymmddToDate(reportData.Day.Base.From.toString())
      : null;

  return (
    <>
      <div className="bg-white">
        <AssetChange {...{ report: reportData as GetReportResult }} />
      </div>
      {!isLinkedLine && isPremium() && (
        <LineNoticeBanner {...{ className: ['mt-4', 'w-1/2', 'w-3/4'] }} />
      )}
      <TransactionsHistory
        {...{
          targetDate,
          onAccountItemClick,
          onTradeItemClick,
        }}
      />
      <TradeList {...{ onTradeItemClick, targetDate }} />
      <NoticeSettingModal
        {...{
          isShow: isShowNoticeSettingModal,
          hide: hideNoticeSettingModal,
        }}
      />
    </>
  );
};

const fetchReport = async () => {
  return await getReport(consts.reportType.日次);
};
